import {
  Card,
  Icon,
  PanelProps,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import {
  DataSourceType,
  SUPPORTED_DATA_SOURCE_FAMILIES,
  UNKNOWN_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { getDataSourceTypeCardTestId } from 'src/constants/testIds';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  DataSourceSelectionPanel,
  DataSourceSelectionPanelProps,
} from 'src/containers/ProjectResourceDialog/DataSourceSelectionPanel/DataSourceSelectionPanel';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { connectionSelector } from 'src/selectors/connection';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import { dataSourceTypeListSelector } from 'src/selectors/dataSourceAvailableTypeList';
import { dataSourceFamiliesByTypeSelector } from 'src/selectors/dataSourceFamiliesByType';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import * as copy from 'src/templates/copy';
import { getDataSourceIcon } from 'src/templates/icons';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceTypeSelectionPanelProps = ProjectResourceDialogPanelProps;

export const DataSourceTypeSelectionPanel: React.VFC<
  PanelProps<DataSourceTypeSelectionPanelProps>
> = ({ closePanel, openPanel, setFooterProps }) => {
  const isMounted = useIsMounted();

  const connection = useReduxSelector(connectionSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshing = useReduxSelector(isRefreshingDataSourcesSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const dataSourceTypeList = useReduxSelector(dataSourceTypeListSelector);
  const isDataSourceDescriptionEndpointSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceDescriptionEndpointSupported',
      connection
    )
  );

  const handleSelectDataSourceType = React.useCallback(
    async (selectedDataSourceType: string) => {
      if (!selectedDataSourceType) {
        return;
      }
      const dataSources = dataSourcesByType[selectedDataSourceType] || [];

      if (!isDataSourceDescriptionEndpointSupported) {
        if (!dataSources.length) {
          return;
        }
        if (dataSources.length === 1) {
          if (connection && !dataSources[0].options) {
            await dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSources[0].id,
              connection
            );
          }
          const props: ProjectVirtualGraphCreationPanelProps = {
            dataSourceId: dataSources[0].id,
            setFooterProps,
          };
          openPanel({
            renderPanel: ProjectVirtualGraphCreationPanel,
            props,
          });
          return;
        }
      }

      if (connection) {
        dataSources.forEach((dataSource) => {
          if (!dataSource.options) {
            dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSource.id,
              connection
            );
          }
        });
      }

      const props: DataSourceSelectionPanelProps = {
        setFooterProps,
        type: selectedDataSourceType,
      };
      openPanel({
        renderPanel: DataSourceSelectionPanel,
        props,
      });
    },
    [
      connection,
      dataSourcesByType,
      isDataSourceDescriptionEndpointSupported,
      openPanel,
      setFooterProps,
    ]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const dataSourceFamiliesByType = useReduxSelector(
    dataSourceFamiliesByTypeSelector(connection)
  );
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);

  const typeList = isDataSourceDescriptionEndpointSupported
    ? Array.from(dataSourceTypeList).filter(
        (dataSourceType) =>
          SUPPORTED_DATA_SOURCE_FAMILIES[
            dataSourceFamiliesByType[dataSourceType]
          ]
      )
    : (Object.keys(dataSourcesByType) as DataSourceType[]).filter(
        (dataSourceType) =>
          SUPPORTED_DATA_SOURCE_FAMILIES[
            dataSourceFamiliesByType[dataSourceType]
          ]
      );
  typeList.sort(
    alphaSort((dataSourceType) => dataSourceLabelsByType[dataSourceType])
  );

  const iconList: React.ReactElement[] = typeList.map((dataSourceType) => {
    const { isBlueprintIcon, icon } = getDataSourceIcon({
      dataSourceType,
      stardogVersion,
    });
    const label =
      dataSourceLabelsByType[dataSourceType] ||
      dataSourceLabelsByType[UNKNOWN_DATA_SOURCE_TYPE];
    return (
      <Card
        key={dataSourceType}
        className={classNames(
          'sd-data-source-panel-cards__card',
          'sd-data-source-panel-cards__card--type'
        )}
        data-testid={getDataSourceTypeCardTestId(dataSourceType)}
        interactive
        onClick={() => {
          handleSelectDataSourceType(dataSourceType);
        }}
      >
        {isBlueprintIcon ? (
          <Icon icon={icon} iconSize={50} />
        ) : (
          <img alt={`${label} icon`} height="50px" src={icon} />
        )}
        <div>{label}</div>
      </Card>
    );
  });

  const fullContent =
    iconList.length === 0 ? (
      <div className="sd-data-source-panel-empty">
        {
          copy.components.projectResourceDialog.dataSourceTypeSelectionPanel
            .noDataSources
        }
      </div>
    ) : (
      <div className="sd-data-source-panel-cards">{iconList}</div>
    );

  return (
    <DataSourcePanelContainer>
      {isConnecting || isRefreshing ? (
        <Spinner size={SpinnerSize.LARGE} />
      ) : (
        fullContent
      )}
    </DataSourcePanelContainer>
  );
};
